import { useEffect, useState } from "react";
import { NoteBand, Page } from "../../shared/ui/Page";
import type { BillingProduct, BillingSubscription, PaidSpaceSlot, SpaceRole } from "../../shared/api/types";
import { isWebDevelopmentControlVisible } from "../../shared/environment";
import { AdminDashboardCard } from "../components/AdminDashboardCard";
import { AdminShell } from "../components/AdminShell";
import { AdminSpacesTable } from "../components/AdminSpacesTable";
import { AdminTable } from "../components/AdminTable";
import { DetailPanel } from "../components/DetailPanel";
import { FreeLimitModeBanner } from "../components/FreeLimitModeBanner";
import { PaidSlotCard } from "../components/PaidSlotCard";
import {
  ADMIN_DASHBOARD_METRICS,
  FREE_LIMIT_MODE_EXAMPLE,
  getMockSpaceAdminDetail,
  JOIN_REQUEST_ROWS,
  MANAGED_SPACES_ROWS,
  type MockInvite,
  type MockJoinRequest,
  type MockMember,
  type MockReport,
  type MockRoom,
  type MockSpace,
  MOCK_ADMIN_DATA,
  PAID_SLOT_USAGE,
  type SpaceAdminDetail,
  SPACE_DASHBOARD_ROWS
} from "../mock/mockData";
import { mockBooplyAdminApi } from "../mock/mockApi";

const SPACE_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "join-requests", label: "Join Requests" },
  { id: "members", label: "Members" },
  { id: "rooms", label: "Rooms" },
  { id: "invites", label: "Invites" },
  { id: "reports", label: "Reports" },
  { id: "retention", label: "Retention" },
  { id: "billing", label: "Billing" },
  { id: "settings", label: "Settings" }
] as const;

export const APPROVAL_ROLE_OPTIONS = ["admin", "moderator", "member", "guest"] as const satisfies readonly SpaceRole[];
export const MEMBER_ROLE_OPTIONS = ["owner", "admin", "moderator", "member", "guest"] as const satisfies readonly SpaceRole[];
export const ROOM_ALLOWED_ROLE_OPTIONS = ["owner", "admin", "moderator", "member", "guest"] as const satisfies readonly SpaceRole[];
export const ROOM_NAME_LIMIT = 30;
export const PLUS_INVITE_LIMIT = 3;
export const SPACE_NAME_LIMIT = 40;
export const SPACE_DESCRIPTION_LIMIT = 180;
export const JOIN_QUESTION_LIMIT = 140;
export const SPACE_RULES_LIMIT = 700;

type ApprovalRole = (typeof APPROVAL_ROLE_OPTIONS)[number];
type InviteCreateKind = "code" | "link" | "guest_pass";

interface RoomDraft {
  readonly name: string;
  readonly description: string;
  readonly roleRestricted: boolean;
  readonly allowedRoles: readonly SpaceRole[];
}

const EMPTY_ROOM_DRAFT: RoomDraft = {
  name: "",
  description: "",
  roleRestricted: false,
  allowedRoles: ["owner", "admin"]
};

interface SpaceSettingsDraft {
  readonly description: string;
  readonly iconLabel: string;
  readonly joinApprovalRequired: boolean;
  readonly joinQuestion: string;
  readonly localDiscoveryEnabled: boolean;
  readonly name: string;
  readonly spaceRules: string;
}

export function LoginPage() {
  return (
    <Page
      eyebrow="Admin access"
      title="Access web admin."
      intro="Use your email to access web admin for Spaces you manage."
    >
      <section className="login-panel" aria-label="Email magic link placeholder">
        <label htmlFor="admin-email">Email</label>
        <input id="admin-email" name="email" placeholder="you@example.com" type="email" />
        <button className="button primary" type="button">Send magic link</button>
        <p>
          Email magic link placeholder only. No real authentication is enabled in
          this shell, and Booply does not use phone-number login.
        </p>
      </section>
      <NoteBand title="No real auth yet">
        <p>
          This route exists so the product site and web admin structure can be
          built without pretending there is production authentication or hardcoded
          admin secrets.
        </p>
      </NoteBand>
    </Page>
  );
}

export function AdminHomePage() {
  return (
    <AdminShell>
      <section className="page-heading">
        <p className="eyebrow">Admin mock mode</p>
        <h1>Manage the Spaces you run.</h1>
        <p>
          This web admin shell uses mock data while real auth and API integration
          continue. It is for Space owners and admins to manage Spaces, join
          requests, reports, paid slot usage, Relay health, and billing alerts.
        </p>
      </section>
      <FreeLimitModeBanner
        graceDaysRemaining={FREE_LIMIT_MODE_EXAMPLE.grace_days_remaining}
        graceDaysTotal={FREE_LIMIT_MODE_EXAMPLE.grace_days_total}
        reason={FREE_LIMIT_MODE_EXAMPLE.reason}
        spaceName={FREE_LIMIT_MODE_EXAMPLE.space_name}
      />
      <section className="admin-grid">
        {ADMIN_DASHBOARD_METRICS.map((metric) => (
          <AdminDashboardCard
            key={metric.title}
            note={metric.note}
            title={metric.title}
            value={metric.value}
          />
        ))}
        <PaidSlotCard
          assigned={PAID_SLOT_USAGE.assigned}
          label={PAID_SLOT_USAGE.label}
          note={PAID_SLOT_USAGE.note}
          total={PAID_SLOT_USAGE.total}
        />
      </section>
      <div className="admin-stack">
        <AdminSpacesTable rows={SPACE_DASHBOARD_ROWS} />
        <DetailPanel title="Paid Space slots">
          <div className="slot-list">
            {MOCK_ADMIN_DATA.paidSpaceSlots.map((slot) => {
              const assignedSpace = MOCK_ADMIN_DATA.spaces.find((space) => space.space_id === slot.assigned_space_id);

              return (
                <article className="slot-row" key={slot.paid_slot_id}>
                  <strong>Slot {slot.slot_index}</strong>
                  <span>{assignedSpace ? assignedSpace.name : "Unassigned"}</span>
                  <small>{slot.status ?? (slot.active ? "active" : "inactive")}</small>
                </article>
              );
            })}
          </div>
        </DetailPanel>
        <AdminTable rows={JOIN_REQUEST_ROWS} title="Pending join requests" />
        <AdminTable rows={MANAGED_SPACES_ROWS} title="Space status summary" />
        <DetailPanel title="Admin privacy boundaries">
          <p>
            Admin placeholders do not display ordinary message plaintext. Report
            review surfaces may later show selected reported content because
            reporting is an explicit moderation exception.
          </p>
          <p>
            Admin tools must not introduce phone-number login, contact upload,
            media messages, DMs, read receipts, or public profile discovery.
          </p>
        </DetailPanel>
      </div>
    </AdminShell>
  );
}

interface AdminSpaceDetailPageProps {
  readonly spaceId: string;
}

export function AdminSpaceDetailPage({ spaceId }: AdminSpaceDetailPageProps) {
  const detail = getMockSpaceAdminDetail(spaceId);

  if (!detail) {
    return (
      <AdminShell>
        <section className="page-heading">
          <p className="eyebrow">Admin mock mode</p>
          <h1>Space not found.</h1>
          <p>The mock data source does not include this Space.</p>
        </section>
        <a className="button secondary" href="/admin">Back to dashboard</a>
      </AdminShell>
    );
  }

  return <AdminSpaceDetailContent detail={detail} />;
}

interface AdminSpaceDetailContentProps {
  readonly detail: SpaceAdminDetail;
}

function AdminSpaceDetailContent({ detail }: AdminSpaceDetailContentProps) {
  const [joinRequests, setJoinRequests] = useState(() => detail.joinRequests.map((request) => ({ ...request })));
  const [members, setMembers] = useState(() => detail.members.map((memberRecord) => ({ ...memberRecord })));
  const [rooms, setRooms] = useState(() => detail.rooms.map((roomRecord) => ({ ...roomRecord })));
  const [invites, setInvites] = useState(() => detail.invites.map((inviteRecord) => ({ ...inviteRecord })));
  const [reports, setReports] = useState(() => detail.reports.map((reportRecord) => ({ ...reportRecord })));
  const [paidSlots, setPaidSlots] = useState(() => MOCK_ADMIN_DATA.paidSpaceSlots.map((slot) => ({ ...slot })));
  const [billingProducts, setBillingProducts] = useState<readonly BillingProduct[]>([]);
  const [fakeSubscriptions, setFakeSubscriptions] = useState<readonly BillingSubscription[]>([]);
  const [retentionDays, setRetentionDays] = useState(spaceRetentionInitialValue(detail.space));
  const [settingsDraft, setSettingsDraft] = useState<SpaceSettingsDraft>(() => ({
    description: detail.space.description ?? "",
    iconLabel: "Icon placeholder",
    joinApprovalRequired: detail.space.join_approval_required,
    joinQuestion: "",
    localDiscoveryEnabled: detail.space.local_discovery_enabled,
    name: detail.space.name,
    spaceRules: ""
  }));
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [billingMessage, setBillingMessage] = useState<string | null>(null);
  const [retentionMessage, setRetentionMessage] = useState<string | null>(null);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [roomDraft, setRoomDraft] = useState<RoomDraft>(EMPTY_ROOM_DRAFT);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [roomFormMessage, setRoomFormMessage] = useState<string | null>(null);
  const [approvalRoles, setApprovalRoles] = useState<Record<string, ApprovalRole>>(() =>
    Object.fromEntries(detail.joinRequests.map((request) => [request.join_request_id, "member" as ApprovalRole]))
  );
  const { space } = detail;
  const currentPaidSlot = paidSlots.find((slot) => slot.assigned_space_id === space.space_id) ?? null;
  const pendingJoinRequestCount = joinRequests.filter((request) => request.status === "pending").length;
  const activeMemberCount = members.filter((memberRecord) => memberRecord.status === "active").length;
  const openReportCount = reports.filter((reportRecord) =>
    reportRecord.status === "open" || reportRecord.status === "reviewing" || reportRecord.status === "in_review"
  ).length;
  const isPaidSpace = space.plan_state === "spaces_paid" || space.plan_state === "paid";
  const retentionOptions = space.retention_options ?? [space.retention_days];

  useEffect(() => {
    let mounted = true;

    void Promise.all([
      mockBooplyAdminApi.getPaidSpaceSlots(),
      mockBooplyAdminApi.getBillingProducts(),
      mockBooplyAdminApi.listReports(space.space_id)
    ]).then(([slotsResponse, productsResponse, reportsResponse]) => {
      if (!mounted) {
        return;
      }

      setPaidSlots(slotsResponse.paid_space_slots.map((slot) => ({ ...slot })));
      setBillingProducts(productsResponse.products);
      setReports(reportsResponse.reports.map((reportRecord) => ({ ...(reportRecord as MockReport) })));
    });

    return () => {
      mounted = false;
    };
  }, [space.space_id]);

  async function approveJoinRequest(requestId: string, role: ApprovalRole = "member") {
    await mockBooplyAdminApi.approveJoinRequest(requestId);
    setApprovalRoles((current) => ({
      ...current,
      [requestId]: role
    }));
    setJoinRequests((current) =>
      current.map((request) =>
        request.join_request_id === requestId
          ? { ...request, resolved_at: new Date().toISOString(), status: "approved" }
          : request
      )
    );
  }

  async function denyJoinRequest(requestId: string) {
    await mockBooplyAdminApi.denyJoinRequest(requestId);
    setJoinRequests((current) =>
      current.map((request) =>
        request.join_request_id === requestId
          ? { ...request, resolved_at: new Date().toISOString(), status: "denied" }
          : request
      )
    );
  }

  function changeMemberRole(memberId: string, role: SpaceRole) {
    setMembers((current) =>
      current.map((memberRecord) =>
        memberRecord.member_id === memberId
          ? { ...memberRecord, role }
          : memberRecord
      )
    );
  }

  function updateMemberStatus(memberId: string, status: MockMember["status"]) {
    setMembers((current) =>
      current.map((memberRecord) =>
        memberRecord.member_id === memberId
          ? {
              ...memberRecord,
              removed_at: status === "active" ? null : new Date().toISOString(),
              status
            }
          : memberRecord
      )
    );
  }

  function resetRoomForm() {
    setEditingRoomId(null);
    setRoomDraft(EMPTY_ROOM_DRAFT);
  }

  function submitRoomForm() {
    const trimmedName = roomDraft.name.trim();
    const trimmedDescription = roomDraft.description.trim();

    if (trimmedName.length === 0) {
      setRoomFormMessage("Room name is required.");
      return;
    }

    if (trimmedName.length > ROOM_NAME_LIMIT) {
      setRoomFormMessage(`Room names can be up to ${ROOM_NAME_LIMIT} characters.`);
      return;
    }

    if (roomDraft.roleRestricted && !isPaidSpace) {
      setRoomFormMessage("Role-based Rooms require a paid Space.");
      return;
    }

    if (!editingRoomId && rooms.length >= space.room_cap) {
      setRoomFormMessage(`This ${space.plan_label} is at its ${space.room_cap} Room limit.`);
      return;
    }

    if (editingRoomId) {
      setRooms((current) =>
        current.map((roomRecord) =>
          roomRecord.room_id === editingRoomId
            ? toUpdatedRoom(roomRecord, trimmedName, trimmedDescription, roomDraft, isPaidSpace)
            : roomRecord
        )
      );
      setRoomFormMessage("Room updated in mock mode.");
      resetRoomForm();
      return;
    }

    setRooms((current) => [
      ...current,
      {
        allowed_roles: roomDraft.roleRestricted && isPaidSpace ? roomDraft.allowedRoles : [],
        created_at: new Date().toISOString(),
        description: trimmedDescription.length > 0 ? trimmedDescription : null,
        frozen: false,
        key_epoch: 1,
        name: trimmedName,
        notice: null,
        required_role: null,
        role_restricted: roomDraft.roleRestricted && isPaidSpace,
        room_id: `room_mock_${Date.now()}`,
        room_type: roomDraft.roleRestricted && isPaidSpace ? "role_based" : "standard",
        space_id: space.space_id,
        updated_at: new Date().toISOString()
      }
    ]);
    setRoomFormMessage("Room created in mock mode.");
    resetRoomForm();
  }

  function editRoom(room: MockRoom) {
    setEditingRoomId(room.room_id);
    setRoomDraft({
      allowedRoles: room.allowed_roles.length > 0 ? room.allowed_roles : ["owner", "admin"],
      description: room.description ?? "",
      name: room.name,
      roleRestricted: room.role_restricted
    });
    setRoomFormMessage(null);
  }

  function toggleRoomFrozen(roomId: string) {
    setRooms((current) =>
      current.map((roomRecord) =>
        roomRecord.room_id === roomId
          ? {
              ...roomRecord,
              frozen: !roomRecord.frozen,
              updated_at: new Date().toISOString()
            }
          : roomRecord
      )
    );
  }

  function deleteRoom(roomId: string) {
    if (rooms.length <= 1) {
      setRoomFormMessage("Every Space must keep at least one Room.");
      return;
    }

    setRooms((current) => current.filter((roomRecord) => roomRecord.room_id !== roomId));
    setRoomFormMessage("Room deleted in mock mode.");
  }

  function createInvite(kind: InviteCreateKind) {
    const activeInvites = invites.filter((inviteRecord) => inviteRecord.revoked_at === null);
    const activeCodesAndLinks = activeInvites.filter((inviteRecord) =>
      inviteRecord.guest_pass_expires_at === null &&
      (inviteRecord.invite_type === "code" || inviteRecord.invite_type === "link")
    );
    const activeCodeCount = activeInvites.filter((inviteRecord) =>
      inviteRecord.guest_pass_expires_at === null && inviteRecord.invite_type === "code"
    ).length;
    const activeLinkCount = activeInvites.filter((inviteRecord) =>
      inviteRecord.guest_pass_expires_at === null && inviteRecord.invite_type === "link"
    ).length;

    if (kind === "guest_pass" && !isPaidSpace) {
      setInviteMessage("Guest Passes are available for paid Spaces only.");
      return;
    }

    if (!isPaidSpace && space.plan_state === "free") {
      if (kind === "code" && activeCodeCount >= 1) {
        setInviteMessage("Free Spaces can have 1 active join code.");
        return;
      }

      if (kind === "link" && activeLinkCount >= 1) {
        setInviteMessage("Free Spaces can have 1 active invite link.");
        return;
      }
    }

    if (!isPaidSpace && space.plan_state === "plus" && activeCodesAndLinks.length >= PLUS_INVITE_LIMIT) {
      setInviteMessage(`Plus Spaces can have up to ${PLUS_INVITE_LIMIT} active join codes or invite links.`);
      return;
    }

    const createdAt = new Date().toISOString();
    const inviteType = kind === "link" ? "link" : kind === "code" ? "code" : "qr";
    const label = kind === "guest_pass"
      ? "Guest Pass mock"
      : kind === "link"
        ? "Invite link mock"
        : "Join code mock";

    setInvites((current) => [
      ...current,
      {
        created_at: createdAt,
        expires_at: "2026-06-23T09:00:00Z",
        guest_pass_expires_at: kind === "guest_pass" ? "2026-06-30T23:59:00Z" : null,
        invite_id: `inv_mock_${kind}_${Date.now()}`,
        invite_type: inviteType,
        label,
        raw_token_once: kind === "link" ? "https://booplyim.com/j/mock-link" : kind === "code" ? "MOCK-7788" : "guest-pass-mock",
        revoked_at: null,
        space_id: space.space_id,
        token_hint: kind === "link" ? "link-****-mock" : kind === "code" ? "MOCK-****" : "guest-****-mock",
        use_count: 0
      }
    ]);
    setInviteMessage(`${label} created in mock mode. Raw token is shown here only at creation in the real API.`);
  }

  function revokeInvite(inviteId: string) {
    setInvites((current) =>
      current.map((inviteRecord) =>
        inviteRecord.invite_id === inviteId
          ? { ...inviteRecord, revoked_at: new Date().toISOString() }
          : inviteRecord
      )
    );
    setInviteMessage("Invite revoked in mock mode.");
  }

  function copyInvite(invite: MockInvite) {
    setInviteMessage(`Copied ${invite.raw_token_once ?? invite.token_hint} in mock mode.`);
  }

  async function dismissReport(reportId: string) {
    if (!confirmReportAction("Dismiss report", "Dismiss this report after review? The submitted report record stays retained according to report retention rules.")) {
      return;
    }

    await mockBooplyAdminApi.patchReport(reportId, {
      status: "dismissed"
    });
    setReports((current) =>
      current.map((reportRecord) =>
        reportRecord.report_id === reportId
          ? { ...reportRecord, resolved_at: new Date().toISOString(), status: "dismissed" }
          : reportRecord
      )
    );
    setReportMessage("Report dismissed in mock/API mode.");
  }

  async function warnFromReport(reportId: string) {
    if (!confirmReportAction("Warn member", "Record a warning placeholder for this report? This does not make AI moderation final or automatic.")) {
      return;
    }

    await mockBooplyAdminApi.patchReport(reportId, {
      moderator_action: "warn_placeholder",
      status: "in_review"
    });
    setReports((current) =>
      current.map((reportRecord) =>
        reportRecord.report_id === reportId
          ? { ...reportRecord, status: "in_review" }
          : reportRecord
      )
    );
    setReportMessage("Warn action recorded in mock/API mode.");
  }

  async function escalateReport(reportId: string) {
    if (!confirmReportAction("Escalate report", "Escalate this report to Booply platform review? Reported content and relevant context may be reviewed.")) {
      return;
    }

    await mockBooplyAdminApi.escalateReport(reportId);
    setReports((current) =>
      current.map((reportRecord) =>
        reportRecord.report_id === reportId
          ? { ...reportRecord, status: reportRecord.status === "open" ? "in_review" : reportRecord.status }
          : reportRecord
      )
    );
    setReportMessage("Report escalated in mock/API mode.");
  }

  function updateMemberFromReport(memberId: string | null | undefined, status: MockMember["status"]) {
    if (!memberId) {
      setReportMessage("This report has no reported member attached.");
      return;
    }

    const actionLabel = status === "banned" ? "Ban member" : "Remove member";
    const actionCopy = status === "banned"
      ? "Ban this member from the Space based on this report? This action stays scoped to this Space."
      : "Remove this member from the Space based on this report? This action stays scoped to this Space.";

    if (!confirmReportAction(actionLabel, actionCopy)) {
      return;
    }

    updateMemberStatus(memberId, status);
    setReportMessage(status === "banned" ? "Reported member banned in mock mode." : "Reported member removed in mock mode.");
  }

  async function assignSlotToCurrentSpace(slotId: string) {
    if (currentPaidSlot) {
      setBillingMessage(`This Space already has paid slot ${currentPaidSlot.slot_index} assigned.`);
      return;
    }

    const response = await mockBooplyAdminApi.assignPaidSlot(slotId, { space_id: space.space_id });

    setPaidSlots((current) =>
      current.map((slot) =>
        slot.paid_slot_id === response.paid_space_slot.paid_slot_id
          ? response.paid_space_slot
          : slot
      )
    );
    setBillingMessage("Paid slot assigned to this Space. Paid Spaces do not count against Free or Plus creation caps.");
  }

  async function unassignSlot(slotId: string) {
    const confirmed = typeof window === "undefined" || window.confirm(
      "Unassign this paid Space slot? If the Space exceeds Free limits later, it may enter Free Limit Mode."
    );

    if (!confirmed) {
      return;
    }

    const response = await mockBooplyAdminApi.unassignPaidSlot(slotId);

    setPaidSlots((current) =>
      current.map((slot) =>
        slot.paid_slot_id === response.paid_space_slot.paid_slot_id
          ? response.paid_space_slot
          : slot
      )
    );
    setBillingMessage("Paid slot unassigned. If this Space exceeds Free limits, it may enter Free Limit Mode.");
  }

  function chooseDowngradeSpacePlaceholder() {
    setBillingMessage("Downgrade Space selection is a placeholder. Production billing will ask which paid Space should keep a slot.");
  }

  async function activateFakeSubscription(productId: BillingProduct["product_id"]) {
    const response = await mockBooplyAdminApi.fakePurchase({ product_id: productId });
    const slotsResponse = await mockBooplyAdminApi.getPaidSpaceSlots();

    setFakeSubscriptions((current) => [response.subscription, ...current]);
    setPaidSlots(slotsResponse.paid_space_slots.map((slot) => ({ ...slot })));
    setBillingMessage(`${formatBillingProductId(productId)} activated in development-only fake billing mode.`);
  }

  async function expireLatestFakeSubscription() {
    const activeSubscription = fakeSubscriptions.find((subscription) => subscription.status !== "expired");

    if (!activeSubscription) {
      setBillingMessage("No active fake subscription is available to expire.");
      return;
    }

    const response = await mockBooplyAdminApi.fakeExpire({
      subscription_id: activeSubscription.subscription_id
    });
    const slotsResponse = await mockBooplyAdminApi.getPaidSpaceSlots();

    setFakeSubscriptions((current) =>
      current.map((subscription) =>
        subscription.subscription_id === response.subscription.subscription_id
          ? response.subscription
          : subscription
      )
    );
    setPaidSlots(slotsResponse.paid_space_slots.map((slot) => ({ ...slot })));
    setBillingMessage("Latest fake subscription expired. Any related paid slots are now inactive.");
  }

  function updateRetention(nextRetentionDays: number) {
    if (!isPaidSpace) {
      setRetentionMessage("Free and Plus Spaces clear messages after a week.");
      return;
    }

    if (!(retentionOptions as readonly number[]).includes(nextRetentionDays)) {
      setRetentionMessage("This retention option is not available for this Space.");
      return;
    }

    setRetentionDays(nextRetentionDays);
    setRetentionMessage(`Retention set to ${nextRetentionDays} days in mock mode.`);
  }

  function updateSettingsDraft(nextDraft: SpaceSettingsDraft) {
    setSettingsDraft(nextDraft);
    setSettingsMessage(null);
  }

  function saveSettings() {
    const trimmedName = settingsDraft.name.trim();
    const trimmedDescription = settingsDraft.description.trim();
    const trimmedJoinQuestion = settingsDraft.joinQuestion.trim();
    const trimmedRules = settingsDraft.spaceRules.trim();

    if (trimmedName.length === 0) {
      setSettingsMessage("Space name is required.");
      return;
    }

    if (trimmedName.length > SPACE_NAME_LIMIT) {
      setSettingsMessage(`Space name can be up to ${SPACE_NAME_LIMIT} characters.`);
      return;
    }

    if (trimmedDescription.length > SPACE_DESCRIPTION_LIMIT) {
      setSettingsMessage(`Description can be up to ${SPACE_DESCRIPTION_LIMIT} characters.`);
      return;
    }

    if (trimmedJoinQuestion.length > JOIN_QUESTION_LIMIT) {
      setSettingsMessage(`Join question can be up to ${JOIN_QUESTION_LIMIT} characters.`);
      return;
    }

    if (trimmedRules.length > SPACE_RULES_LIMIT) {
      setSettingsMessage(`Space rules can be up to ${SPACE_RULES_LIMIT} characters.`);
      return;
    }

    setSettingsDraft({
      ...settingsDraft,
      description: trimmedDescription,
      joinQuestion: trimmedJoinQuestion,
      name: trimmedName,
      spaceRules: trimmedRules
    });
    setSettingsMessage("Settings saved in mock mode.");
  }

  return (
    <AdminShell>
      <section className="page-heading">
        <p className="eyebrow">Admin Space detail</p>
        <h1>{settingsDraft.name}</h1>
        <p>
          {space.plan_label} with {activeMemberCount} of {space.member_cap} active members,
          {" "}{rooms.length} of {space.room_cap} Rooms, and {retentionDays} days retention.
        </p>
      </section>
      {space.free_limit_mode ? (
        <FreeLimitModeBanner
          graceDaysRemaining={FREE_LIMIT_MODE_EXAMPLE.grace_days_remaining}
          graceDaysTotal={FREE_LIMIT_MODE_EXAMPLE.grace_days_total}
          reason={FREE_LIMIT_MODE_EXAMPLE.reason}
          spaceName={settingsDraft.name}
        />
      ) : null}
      <nav className="admin-tabs" aria-label="Space detail tabs">
        {SPACE_DETAIL_TABS.map((tab) => (
          <a className={tab.id === "overview" ? "admin-tab active" : "admin-tab"} href={`#${tab.id}`} key={tab.id}>
            {tab.label}
          </a>
        ))}
      </nav>
      <div className="admin-stack">
        <DetailPanel id="overview" title="Overview">
          <div className="space-overview-grid">
            <OverviewFact label="Plan state" value={space.plan_label} />
            <OverviewFact label="Members" value={`${activeMemberCount}/${space.member_cap}`} />
            <OverviewFact label="Rooms" value={`${rooms.length}/${space.room_cap}`} />
            <OverviewFact label="Retention" value={`${retentionDays} days`} />
            <OverviewFact label="Relay status" value={detail.relayStatusLabel} />
            <OverviewFact label="In-room discovery" value={settingsDraft.localDiscoveryEnabled ? "Enabled" : "Off"} />
            <OverviewFact label="Your role" value={detail.currentUserRole} />
            <OverviewFact label="Pending Join Requests" value={String(pendingJoinRequestCount)} />
            <OverviewFact label="Open reports" value={String(openReportCount)} />
            <OverviewFact label="Paid slot" value={currentPaidSlot ? `Slot ${currentPaidSlot.slot_index}` : "None assigned"} />
          </div>
        </DetailPanel>
        <DetailPanel id="join-requests" title="Join Requests">
          <div className="join-request-list">
            {joinRequests.map((request) => (
              <JoinRequestCard
                approvalRole={approvalRoles[request.join_request_id] ?? "member"}
                key={request.join_request_id}
                onApprove={(role = "member") => {
                  void approveJoinRequest(request.join_request_id, role);
                }}
                onDeny={() => {
                  void denyJoinRequest(request.join_request_id);
                }}
                onRoleChange={(role) => {
                  setApprovalRoles((current) => ({
                    ...current,
                    [request.join_request_id]: role
                  }));
                }}
                request={request}
              />
            ))}
          </div>
        </DetailPanel>
        <DetailPanel id="members" title="Members">
          <MembersAdminTable
            members={members}
            onBanMember={(memberId) => updateMemberStatus(memberId, "banned")}
            onChangeRole={changeMemberRole}
            onRemoveMember={(memberId) => updateMemberStatus(memberId, "removed")}
          />
        </DetailPanel>
        <DetailPanel id="rooms" title="Rooms">
          <RoomsAdminTab
            canUseRoleRestrictedRooms={isPaidSpace}
            draft={roomDraft}
            editingRoomId={editingRoomId}
            message={roomFormMessage}
            onCancelEdit={resetRoomForm}
            onDeleteRoom={deleteRoom}
            onDraftChange={setRoomDraft}
            onEditRoom={editRoom}
            onSubmitRoom={submitRoomForm}
            onToggleFrozen={toggleRoomFrozen}
            roomCap={space.room_cap}
            rooms={rooms}
            spaceLabel={space.plan_label}
          />
        </DetailPanel>
        <DetailPanel id="invites" title="Invites">
          <InvitesAdminTab
            invites={invites}
            isPaidSpace={isPaidSpace}
            message={inviteMessage}
            onCopyInvite={copyInvite}
            onCreateInvite={createInvite}
            onRevokeInvite={revokeInvite}
            planState={space.plan_state}
            spaceLabel={space.plan_label}
          />
        </DetailPanel>
        <DetailPanel id="reports" title="Reports">
          <ReportsAdminTab
            members={members}
            message={reportMessage}
            onBanMember={(memberId) => updateMemberFromReport(memberId, "banned")}
            onDismissReport={dismissReport}
            onRemoveMember={(memberId) => updateMemberFromReport(memberId, "removed")}
            onEscalateReport={escalateReport}
            onWarnReport={warnFromReport}
            reports={reports}
          />
        </DetailPanel>
        <DetailPanel id="retention" title="Retention">
          <RetentionAdminTab
            isPaidSpace={isPaidSpace}
            message={retentionMessage}
            onRetentionChange={updateRetention}
            retentionDays={retentionDays}
            retentionOptions={retentionOptions}
            spaceLabel={space.plan_label}
            startedAt={space.created_at}
          />
        </DetailPanel>
        <DetailPanel id="billing" title="Billing">
          <BillingAdminTab
            currentSpace={space}
            currentSpaceSlot={currentPaidSlot}
            message={billingMessage}
            onAssignSlot={assignSlotToCurrentSpace}
            onChooseDowngradeSpace={chooseDowngradeSpacePlaceholder}
            onExpireFakeSubscription={expireLatestFakeSubscription}
            onFakePurchase={activateFakeSubscription}
            onUnassignSlot={unassignSlot}
            billingProducts={billingProducts}
            fakeSubscriptions={fakeSubscriptions}
            paidSlots={paidSlots}
            showDevBillingControls={isDevBillingControlVisible()}
            spaces={MOCK_ADMIN_DATA.spaces}
          />
        </DetailPanel>
        <DetailPanel id="settings" title="Settings">
          <SettingsAdminTab
            draft={settingsDraft}
            freeLimitMode={space.free_limit_mode}
            message={settingsMessage}
            onDraftChange={updateSettingsDraft}
            onSave={saveSettings}
            visibility={space.visibility}
          />
        </DetailPanel>
        <DetailPanel title="Privacy boundary">
          <p>
            This Space detail view shows operational admin data only. It does not
            include a message timeline or ordinary message plaintext.
          </p>
        </DetailPanel>
      </div>
    </AdminShell>
  );
}

interface JoinRequestCardProps {
  readonly approvalRole: ApprovalRole;
  readonly onApprove: (role?: ApprovalRole) => void;
  readonly onDeny: () => void;
  readonly onRoleChange: (role: ApprovalRole) => void;
  readonly request: MockJoinRequest;
}

function JoinRequestCard({
  approvalRole,
  onApprove,
  onDeny,
  onRoleChange,
  request
}: JoinRequestCardProps) {
  const isPending = request.status === "pending";

  return (
    <article className="join-request-card">
      <div className="join-request-card-header">
        <span aria-hidden="true" className="avatar-placeholder">
          {initials(request.profile_display_name ?? request.profile_handle ?? request.profile_id)}
        </span>
        <div>
          <div className="join-request-title-row">
            <h3>{request.profile_display_name ?? "Unnamed profile"}</h3>
            <span className="status-chip quiet">Admin approval</span>
          </div>
          {request.profile_handle ? <p>@{request.profile_handle}</p> : null}
          <span className={request.status === "pending" ? "status-chip warning" : "status-chip success"}>
            {request.status}
          </span>
          <p className="admin-approval-note">
            Approval keeps this Space private. No phone numbers or contacts are exposed here.
          </p>
        </div>
      </div>
      <dl className="join-request-facts">
        <div>
          <dt>Join method</dt>
          <dd>{request.join_method}</dd>
        </div>
        <div>
          <dt>Invite source</dt>
          <dd>{request.invite_source}</dd>
        </div>
        <div>
          <dt>Answer</dt>
          <dd>{request.application_answer}</dd>
        </div>
        <div>
          <dt>Verification state</dt>
          <dd>{formatVerificationState(request.verification_state)}</dd>
        </div>
        <div>
          <dt>Device fingerprint</dt>
          <dd>{request.device_fingerprint}</dd>
        </div>
        <div>
          <dt>Profile age</dt>
          <dd>{request.profile_age_label}</dd>
        </div>
        <div>
          <dt>Prior ban in this Space</dt>
          <dd>{request.prior_ban_in_space ? "Yes" : "No"}</dd>
        </div>
      </dl>
      <div className="join-request-actions">
        <button className="button primary" disabled={!isPending} onClick={() => onApprove("member")} type="button">
          Approve
        </button>
        <label className="role-approval-control">
          <span>Approve as role</span>
          <select
            disabled={!isPending}
            onChange={(event) => onRoleChange(event.target.value as ApprovalRole)}
            value={approvalRole}
          >
            {APPROVAL_ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </label>
        <button className="button secondary" disabled={!isPending} onClick={() => onApprove(approvalRole)} type="button">
          Approve as role
        </button>
        <button className="button danger-subtle" disabled={!isPending} onClick={onDeny} type="button">
          Deny
        </button>
        <button className="button secondary" disabled type="button">
          Ask question placeholder
        </button>
        <button className="button secondary" disabled type="button">
          Block placeholder
        </button>
      </div>
      {request.status === "approved" ? (
        <p className="join-request-result">Approved as {approvalRole} in mock mode.</p>
      ) : null}
      {request.status === "denied" ? (
        <p className="join-request-result">Denied in mock mode.</p>
      ) : null}
    </article>
  );
}

interface MembersAdminTableProps {
  readonly members: readonly MockMember[];
  readonly onBanMember: (memberId: string) => void;
  readonly onChangeRole: (memberId: string, role: SpaceRole) => void;
  readonly onRemoveMember: (memberId: string) => void;
}

function MembersAdminTable({
  members,
  onBanMember,
  onChangeRole,
  onRemoveMember
}: MembersAdminTableProps) {
  return (
    <div className="members-table-wrap">
      <table className="admin-table members-admin-table">
        <thead>
          <tr>
            <th scope="col">Display name</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">Guest expiry</th>
            <th scope="col">Joined</th>
            <th scope="col">Device fingerprint</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isOwner = member.role === "owner";
            const isActive = member.status === "active";

            return (
              <tr key={member.member_id}>
                <td>
                  <strong>{member.display_name ?? member.profile_id}</strong>
                  {member.handle ? <span className="member-handle">@{member.handle}</span> : null}
                </td>
                <td>
                  <label className="member-role-control">
                    <span className="visually-hidden">Change role for {member.display_name ?? member.profile_id}</span>
                    <select
                      disabled={!isActive}
                      onChange={(event) => onChangeRole(member.member_id, event.target.value as SpaceRole)}
                      value={member.role}
                    >
                      {MEMBER_ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </label>
                </td>
                <td>
                  <span className={statusChipClass(member.status)}>{member.status}</span>
                </td>
                <td>{member.guest_expires_at ? formatDate(member.guest_expires_at) : "Not applicable"}</td>
                <td>{member.joined_at ? formatDate(member.joined_at) : "Unknown"}</td>
                <td>{member.device_fingerprint}</td>
                <td>
                  <div className="member-actions">
                    <button
                      className="button danger-subtle"
                      disabled={isOwner || !isActive}
                      onClick={() => onRemoveMember(member.member_id)}
                      title={isOwner ? "Transfer ownership before removing this owner." : undefined}
                      type="button"
                    >
                      Remove member
                    </button>
                    <button
                      className="button danger-subtle"
                      disabled={isOwner || !isActive}
                      onClick={() => onBanMember(member.member_id)}
                      title={isOwner ? "Transfer ownership before banning this owner." : undefined}
                      type="button"
                    >
                      Ban member
                    </button>
                    <button className="button secondary" disabled type="button">
                      Revoke device placeholder
                    </button>
                    {isOwner ? <small>Ownership transfer required before removal.</small> : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface RoomsAdminTabProps {
  readonly canUseRoleRestrictedRooms: boolean;
  readonly draft: RoomDraft;
  readonly editingRoomId: string | null;
  readonly message: string | null;
  readonly onCancelEdit: () => void;
  readonly onDeleteRoom: (roomId: string) => void;
  readonly onDraftChange: (draft: RoomDraft) => void;
  readonly onEditRoom: (room: MockRoom) => void;
  readonly onSubmitRoom: () => void;
  readonly onToggleFrozen: (roomId: string) => void;
  readonly roomCap: number;
  readonly rooms: readonly MockRoom[];
  readonly spaceLabel: string;
}

function RoomsAdminTab({
  canUseRoleRestrictedRooms,
  draft,
  editingRoomId,
  message,
  onCancelEdit,
  onDeleteRoom,
  onDraftChange,
  onEditRoom,
  onSubmitRoom,
  onToggleFrozen,
  roomCap,
  rooms,
  spaceLabel
}: RoomsAdminTabProps) {
  const atRoomCap = rooms.length >= roomCap && editingRoomId === null;
  const roleRestrictionDisabled = !canUseRoleRestrictedRooms;

  return (
    <div className="rooms-admin-stack">
      <div className="room-limit-note">
        <strong>
          {rooms.length}/{roomCap} Rooms used
        </strong>
        <p>
          {spaceLabel} limit: {roomCap} Rooms. Room names can be up to {ROOM_NAME_LIMIT} characters.
          Role-based Rooms are available only on paid Spaces.
          Rooms are channels inside a Space, not DMs.
        </p>
        {atRoomCap ? <p>This Space is at its Room limit. Delete a Room before creating another.</p> : null}
      </div>
      <div className="room-form">
        <div>
          <label htmlFor="room-name">Room name</label>
          <input
            id="room-name"
            maxLength={ROOM_NAME_LIMIT}
            onChange={(event) => onDraftChange({ ...draft, name: event.target.value })}
            placeholder="Announcements"
            value={draft.name}
          />
          <small>{draft.name.length}/{ROOM_NAME_LIMIT}</small>
        </div>
        <div>
          <label htmlFor="room-description">Description</label>
          <input
            id="room-description"
            onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
            placeholder="Important updates"
            value={draft.description}
          />
        </div>
        <label className="room-checkbox">
          <input
            checked={draft.roleRestricted && canUseRoleRestrictedRooms}
            disabled={roleRestrictionDisabled}
            onChange={(event) => onDraftChange({ ...draft, roleRestricted: event.target.checked })}
            type="checkbox"
          />
          <span>Role-based Room</span>
        </label>
        <fieldset className="room-role-fieldset" disabled={!draft.roleRestricted || roleRestrictionDisabled}>
          <legend>Allowed roles</legend>
          {ROOM_ALLOWED_ROLE_OPTIONS.map((role) => (
            <label key={role}>
              <input
                checked={draft.allowedRoles.includes(role)}
                onChange={(event) => onDraftChange({
                  ...draft,
                  allowedRoles: toggleRole(draft.allowedRoles, role, event.target.checked)
                })}
                type="checkbox"
              />
              <span>{role}</span>
            </label>
          ))}
        </fieldset>
        <div className="room-form-actions">
          <button className="button primary" disabled={atRoomCap || draft.name.trim().length === 0} onClick={onSubmitRoom} type="button">
            {editingRoomId ? "Save Room" : "Create Room"}
          </button>
          {editingRoomId ? (
            <button className="button secondary" onClick={onCancelEdit} type="button">
              Cancel edit
            </button>
          ) : null}
        </div>
        {roleRestrictionDisabled ? <p className="form-note">Role-based Room option disabled for Free and Plus Spaces.</p> : null}
        {message ? <p className="form-note">{message}</p> : null}
      </div>
      <div className="rooms-table-wrap">
        <table className="admin-table rooms-admin-table">
          <thead>
            <tr>
              <th scope="col">Room name</th>
              <th scope="col">Description</th>
              <th scope="col">Type</th>
              <th scope="col">Role restriction</th>
              <th scope="col">Allowed roles</th>
              <th scope="col">Frozen</th>
              <th scope="col">Notice</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((room) => {
              const cannotDeleteFinalRoom = rooms.length <= 1;

              return (
                <tr key={room.room_id}>
                  <td><strong>{room.name}</strong></td>
                  <td>{room.description ?? "No description"}</td>
                  <td>{room.room_type === "role_based" ? "Role-based" : "Standard"}</td>
                  <td>{room.role_restricted ? "Restricted" : "Open to members"}</td>
                  <td>{room.allowed_roles.length > 0 ? room.allowed_roles.join(", ") : "All Space members"}</td>
                  <td>{room.frozen ? "Frozen" : "Open"}</td>
                  <td>{room.notice ?? "Notice placeholder not set"}</td>
                  <td>
                    <div className="room-row-actions">
                      <button className="button secondary" onClick={() => onEditRoom(room)} type="button">
                        Edit Room
                      </button>
                      <button className="button secondary" onClick={() => onToggleFrozen(room.room_id)} type="button">
                        {room.frozen ? "Unfreeze placeholder" : "Freeze placeholder"}
                      </button>
                      <button
                        className="button danger-subtle"
                        disabled={cannotDeleteFinalRoom}
                        onClick={() => onDeleteRoom(room.room_id)}
                        title={cannotDeleteFinalRoom ? "Every Space must keep at least one Room." : undefined}
                        type="button"
                      >
                        Delete Room
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface InvitesAdminTabProps {
  readonly invites: readonly MockInvite[];
  readonly isPaidSpace: boolean;
  readonly message: string | null;
  readonly onCopyInvite: (invite: MockInvite) => void;
  readonly onCreateInvite: (kind: InviteCreateKind) => void;
  readonly onRevokeInvite: (inviteId: string) => void;
  readonly planState: string;
  readonly spaceLabel: string;
}

function InvitesAdminTab({
  invites,
  isPaidSpace,
  message,
  onCopyInvite,
  onCreateInvite,
  onRevokeInvite,
  planState,
  spaceLabel
}: InvitesAdminTabProps) {
  const activeInvites = invites.filter((invite) => invite.revoked_at === null);
  const activeJoinCodes = activeInvites.filter((invite) => invite.guest_pass_expires_at === null && invite.invite_type === "code");
  const activeInviteLinks = activeInvites.filter((invite) => invite.guest_pass_expires_at === null && invite.invite_type === "link");
  const activeQrInvites = activeInvites.filter((invite) => invite.guest_pass_expires_at === null && invite.invite_type === "qr");
  const activeGuestPasses = activeInvites.filter((invite) => invite.guest_pass_expires_at !== null);
  const activeCodesAndLinks = activeJoinCodes.length + activeInviteLinks.length;
  const codeDisabled = planState === "free" && activeJoinCodes.length >= 1 || planState === "plus" && activeCodesAndLinks >= PLUS_INVITE_LIMIT;
  const linkDisabled = planState === "free" && activeInviteLinks.length >= 1 || planState === "plus" && activeCodesAndLinks >= PLUS_INVITE_LIMIT;

  return (
    <div className="invites-admin-stack">
      <div className="invite-limit-note">
        <strong>{spaceLabel} invite limits</strong>
        <p>{inviteLimitCopy(planState, isPaidSpace)}</p>
        <p>Invite flows do not create public or global discovery.</p>
      </div>
      <div className="invite-actions">
        <button className="button primary" disabled={codeDisabled} onClick={() => onCreateInvite("code")} type="button">
          Create join code
        </button>
        <button className="button primary" disabled={linkDisabled} onClick={() => onCreateInvite("link")} type="button">
          Create invite link
        </button>
        <button className="button secondary" disabled={!isPaidSpace} onClick={() => onCreateInvite("guest_pass")} type="button">
          Create Guest Pass
        </button>
        <button className="button secondary" disabled={!isPaidSpace} type="button">
          QR poster placeholder
        </button>
      </div>
      {!isPaidSpace ? <p className="form-note">Guest Passes and QR poster tools are available for paid Spaces only.</p> : null}
      {message ? <p className="form-note">{message}</p> : null}
      <div className="invite-section-grid">
        <InviteSection invites={activeJoinCodes} onCopyInvite={onCopyInvite} onRevokeInvite={onRevokeInvite} title="Active join codes" />
        <InviteSection invites={activeInviteLinks} onCopyInvite={onCopyInvite} onRevokeInvite={onRevokeInvite} title="Active invite links" />
        <InviteSection invites={activeQrInvites} onCopyInvite={onCopyInvite} onRevokeInvite={onRevokeInvite} title="QR invite" />
        {isPaidSpace ? (
          <InviteSection invites={activeGuestPasses} onCopyInvite={onCopyInvite} onRevokeInvite={onRevokeInvite} title="Guest Passes" />
        ) : null}
      </div>
      <div className="revoked-invite-list">
        <strong>Revoked invites</strong>
        <span>{invites.filter((invite) => invite.revoked_at !== null).length} revoked in mock mode</span>
      </div>
    </div>
  );
}

interface InviteSectionProps {
  readonly invites: readonly MockInvite[];
  readonly onCopyInvite: (invite: MockInvite) => void;
  readonly onRevokeInvite: (inviteId: string) => void;
  readonly title: string;
}

function InviteSection({ invites, onCopyInvite, onRevokeInvite, title }: InviteSectionProps) {
  return (
    <section className="invite-section">
      <h3>{title}</h3>
      {invites.length === 0 ? <p>No active invites in this category.</p> : null}
      {invites.map((invite) => (
        <article className="invite-card" key={invite.invite_id}>
          <div>
            <strong>{invite.label}</strong>
            <span>{invite.token_hint}</span>
          </div>
          <dl>
            <div>
              <dt>Uses</dt>
              <dd>{invite.use_count ?? 0}</dd>
            </div>
            <div>
              <dt>Expires</dt>
              <dd>{formatDate(invite.guest_pass_expires_at ?? invite.expires_at)}</dd>
            </div>
            <div>
              <dt>Raw mock token</dt>
              <dd>{invite.raw_token_once ?? "Hidden after creation"}</dd>
            </div>
          </dl>
          <div className="invite-card-actions">
            <button className="button secondary" onClick={() => onCopyInvite(invite)} type="button">
              Copy invite
            </button>
            <button className="button secondary" onClick={() => onRevokeInvite(invite.invite_id)} type="button">
              Revoke invite
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}

interface ReportsAdminTabProps {
  readonly members: readonly MockMember[];
  readonly message: string | null;
  readonly onBanMember: (memberId: string | null | undefined) => void;
  readonly onDismissReport: (reportId: string) => void;
  readonly onEscalateReport: (reportId: string) => void;
  readonly onRemoveMember: (memberId: string | null | undefined) => void;
  readonly onWarnReport: (reportId: string) => void;
  readonly reports: readonly MockReport[];
}

function ReportsAdminTab({
  members,
  message,
  onBanMember,
  onDismissReport,
  onEscalateReport,
  onRemoveMember,
  onWarnReport,
  reports
}: ReportsAdminTabProps) {
  return (
    <div className="reports-admin-stack">
      <div className="report-review-note">
        <strong>Review Queue</strong>
        <p>
          Reported content may be visible here because a member explicitly
          submitted it for review. This queue does not show ordinary message
          timelines or cross-Space reputation metrics.
        </p>
        <p>AI moderation is a placeholder signal for human review, not a final or automatic decision.</p>
      </div>
      {message ? <p className="form-note">{message}</p> : null}
      <div className="report-card-list">
        {reports.length === 0 ? <p className="form-note">No reports in this mock Space.</p> : null}
        {reports.map((report) => {
          const reporter = members.find((member) => member.member_id === report.reporter_member_id);
          const reportedUser = members.find((member) => member.member_id === report.reported_member_id);

          return (
            <ReportCard
              key={report.report_id}
              onBanMember={() => onBanMember(report.reported_member_id)}
              onDismissReport={() => onDismissReport(report.report_id)}
              onEscalateReport={() => onEscalateReport(report.report_id)}
              onRemoveMember={() => onRemoveMember(report.reported_member_id)}
              onWarnReport={() => onWarnReport(report.report_id)}
              report={report}
              reportedUserLabel={reportedUser ? memberDisplayLabel(reportedUser) : "No member attached"}
              reporterLabel={reporter ? memberDisplayLabel(reporter) : "Unknown reporter"}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ReportCardProps {
  readonly onBanMember: () => void;
  readonly onDismissReport: () => void;
  readonly onEscalateReport: () => void;
  readonly onRemoveMember: () => void;
  readonly onWarnReport: () => void;
  readonly report: MockReport;
  readonly reportedUserLabel: string;
  readonly reporterLabel: string;
}

function ReportCard({
  onBanMember,
  onDismissReport,
  onEscalateReport,
  onRemoveMember,
  onWarnReport,
  report,
  reportedUserLabel,
  reporterLabel
}: ReportCardProps) {
  const canActOnMember = report.reported_member_id !== null && report.reported_member_id !== undefined;
  const canDismiss = report.status !== "dismissed" && report.status !== "resolved";

  return (
    <article className="report-card">
      <div className="report-card-header">
        <div>
          <h3>{formatReportReason(report.category)}</h3>
          <p>Created {formatDate(report.submitted_at)}</p>
        </div>
        <div className="report-status-stack">
          <span className={report.severity === "severe" ? "status-chip error" : "status-chip warning"}>
            {report.severity}
          </span>
          <span className="status-chip quiet">{report.status}</span>
        </div>
      </div>
      <dl className="report-facts">
        <div>
          <dt>Reason</dt>
          <dd>{formatReportReason(report.category)}</dd>
        </div>
        <div>
          <dt>Reported user</dt>
          <dd>{reportedUserLabel}</dd>
        </div>
        <div>
          <dt>Reporter</dt>
          <dd>{reporterLabel}</dd>
        </div>
        <div>
          <dt>AI moderation result placeholder</dt>
          <dd>{report.ai_moderation_result}</dd>
        </div>
      </dl>
      <div className="submitted-report-content">
        <strong>Submitted content</strong>
        {report.submitted_plaintext.length > 0 ? (
          <p>{report.submitted_plaintext}</p>
        ) : (
          <p>No content excerpt was submitted with this report.</p>
        )}
        <small>{report.review_note}</small>
      </div>
      <div className="report-actions">
        <button className="button secondary" disabled={!canDismiss} onClick={onDismissReport} type="button">
          Dismiss
        </button>
        <button className="button secondary" disabled={!canDismiss} onClick={onWarnReport} type="button">
          Warn
        </button>
        <button className="button danger-subtle" disabled type="button">
          Remove content placeholder
        </button>
        <button className="button danger-subtle" disabled={!canActOnMember} onClick={onRemoveMember} type="button">
          Remove member
        </button>
        <button className="button danger-subtle" disabled={!canActOnMember} onClick={onBanMember} type="button">
          Ban member
        </button>
        <button className="button secondary" disabled={!canDismiss} onClick={onEscalateReport} type="button">
          Escalate
        </button>
      </div>
    </article>
  );
}

interface RetentionAdminTabProps {
  readonly isPaidSpace: boolean;
  readonly message: string | null;
  readonly onRetentionChange: (retentionDays: number) => void;
  readonly retentionDays: number;
  readonly retentionOptions: readonly number[];
  readonly spaceLabel: string;
  readonly startedAt: string;
}

function RetentionAdminTab({
  isPaidSpace,
  message,
  onRetentionChange,
  retentionDays,
  retentionOptions,
  spaceLabel,
  startedAt
}: RetentionAdminTabProps) {
  return (
    <div className="retention-admin-stack">
      <div className="retention-note">
        <strong>Message retention</strong>
        <p>Retention applies to ordinary messages, not the Space itself.</p>
        <p>
          {isPaidSpace
            ? "Paid Spaces can choose 7, 14, or 30 days."
            : `${spaceLabel} retention is fixed at 7 days.`}
        </p>
      </div>
      <div className="space-overview-grid">
        <OverviewFact label="Current retention" value={`${retentionDays} days`} />
        <OverviewFact label="Available options" value={retentionOptions.join(", ")} />
        <OverviewFact label="Started" value={formatDate(startedAt)} />
      </div>
      <fieldset className="retention-option-list">
        <legend>Retention options</legend>
        {retentionOptions.map((option) => (
          <label key={option}>
            <input
              checked={retentionDays === option}
              disabled={!isPaidSpace}
              name="retention-days"
              onChange={() => onRetentionChange(option)}
              type="radio"
            />
            <span>{option} days</span>
          </label>
        ))}
      </fieldset>
      {!isPaidSpace ? <p className="form-note">Free and Plus retention selection is disabled in this mock admin shell.</p> : null}
      {message ? <p className="form-note">{message}</p> : null}
    </div>
  );
}

interface SettingsAdminTabProps {
  readonly draft: SpaceSettingsDraft;
  readonly freeLimitMode: boolean;
  readonly message: string | null;
  readonly onDraftChange: (draft: SpaceSettingsDraft) => void;
  readonly onSave: () => void;
  readonly visibility: string;
}

function SettingsAdminTab({
  draft,
  freeLimitMode,
  message,
  onDraftChange,
  onSave,
  visibility
}: SettingsAdminTabProps) {
  return (
    <div className="settings-admin-stack">
      <div className="settings-note">
        <strong>Space settings</strong>
        <p>In-room discovery is off by default. Public or global discovery is not available.</p>
      </div>
      <div className="settings-form-grid">
        <label>
          Space name
          <input
            maxLength={SPACE_NAME_LIMIT}
            onChange={(event) => onDraftChange({ ...draft, name: event.target.value })}
            type="text"
            value={draft.name}
          />
          <small>{draft.name.length}/{SPACE_NAME_LIMIT}</small>
        </label>
        <label>
          Icon placeholder
          <input
            onChange={(event) => onDraftChange({ ...draft, iconLabel: event.target.value })}
            type="text"
            value={draft.iconLabel}
          />
          <small>Future app icon selection placeholder.</small>
        </label>
        <label className="settings-wide-field">
          Description
          <textarea
            maxLength={SPACE_DESCRIPTION_LIMIT}
            onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
            rows={3}
            value={draft.description}
          />
          <small>{draft.description.length}/{SPACE_DESCRIPTION_LIMIT}</small>
        </label>
        <label className="settings-checkbox">
          <input
            checked={draft.joinApprovalRequired}
            onChange={(event) => onDraftChange({ ...draft, joinApprovalRequired: event.target.checked })}
            type="checkbox"
          />
          <span>Join approval required</span>
        </label>
        <label className="settings-checkbox">
          <input
            checked={draft.localDiscoveryEnabled}
            onChange={(event) => onDraftChange({ ...draft, localDiscoveryEnabled: event.target.checked })}
            type="checkbox"
          />
          <span>In-room discovery</span>
        </label>
        {draft.localDiscoveryEnabled ? (
          <p className="settings-caution settings-wide-field">
            In-room discovery can help nearby participants find this Space in person.
            It does not create public or global discovery.
          </p>
        ) : null}
        <label className="settings-wide-field">
          Join question
          <input
            maxLength={JOIN_QUESTION_LIMIT}
            onChange={(event) => onDraftChange({ ...draft, joinQuestion: event.target.value })}
            placeholder="Optional question for new join requests"
            type="text"
            value={draft.joinQuestion}
          />
          <small>Optional. {draft.joinQuestion.length}/{JOIN_QUESTION_LIMIT}</small>
        </label>
        <label className="settings-wide-field">
          Space rules
          <textarea
            maxLength={SPACE_RULES_LIMIT}
            onChange={(event) => onDraftChange({ ...draft, spaceRules: event.target.value })}
            placeholder="Short rules shown before someone requests to join"
            rows={5}
            value={draft.spaceRules}
          />
          <small>{draft.spaceRules.length}/{SPACE_RULES_LIMIT}</small>
        </label>
      </div>
      <div className="space-overview-grid">
        <OverviewFact label="Visibility" value={visibility} />
        <OverviewFact label="In-room discovery" value={draft.localDiscoveryEnabled ? "Enabled" : "Off"} />
        <OverviewFact label="Join approval" value={draft.joinApprovalRequired ? "Required" : "Off"} />
        <OverviewFact label="Free Limit Mode" value={freeLimitMode ? "Active" : "Off"} />
      </div>
      <div className="settings-actions">
        <button className="button primary" onClick={onSave} type="button">
          Save settings
        </button>
      </div>
      {message ? <p className="form-note">{message}</p> : null}
      <div className="danger-zone-placeholder">
        <strong>Danger Zone placeholder</strong>
        <p>Future controls for freezing or deleting a Space will live here after policy and recovery flows are designed.</p>
      </div>
    </div>
  );
}

interface BillingAdminTabProps {
  readonly billingProducts: readonly BillingProduct[];
  readonly currentSpace: MockSpace;
  readonly currentSpaceSlot: PaidSpaceSlot | null;
  readonly fakeSubscriptions: readonly BillingSubscription[];
  readonly message: string | null;
  readonly onAssignSlot: (slotId: string) => void | Promise<void>;
  readonly onChooseDowngradeSpace: () => void;
  readonly onExpireFakeSubscription: () => void | Promise<void>;
  readonly onFakePurchase: (productId: BillingProduct["product_id"]) => void | Promise<void>;
  readonly onUnassignSlot: (slotId: string) => void | Promise<void>;
  readonly paidSlots: readonly PaidSpaceSlot[];
  readonly showDevBillingControls: boolean;
  readonly spaces: readonly MockSpace[];
}

function BillingAdminTab({
  billingProducts,
  currentSpace,
  currentSpaceSlot,
  fakeSubscriptions,
  message,
  onAssignSlot,
  onChooseDowngradeSpace,
  onExpireFakeSubscription,
  onFakePurchase,
  onUnassignSlot,
  paidSlots,
  showDevBillingControls,
  spaces
}: BillingAdminTabProps) {
  const activeSlots = paidSlots.filter((slot) => slot.active);
  const assignedSlots = activeSlots.filter((slot) => slot.assigned_space_id !== null);
  const unassignedSlots = activeSlots.filter((slot) => slot.assigned_space_id === null);
  const assignedPaidSpaces = spaces.filter((space) => space.paid_slot_id !== null || space.plan_state === "spaces_paid");
  const downgradePressure = assignedPaidSpaces.length > activeSlots.length;
  const subscriptionLabel = activeSlots.length === 1 ? "Booply Spaces 1" : activeSlots.length >= 3 ? "Booply Spaces 3" : "No active Spaces subscription";
  const subscriptionPrice = activeSlots.length === 1 ? "$12.99/month" : activeSlots.length >= 3 ? "$38.99/month" : "No paid slot subscription";
  const monthlyPerPaidSpace = activeSlots.length === 0
    ? "A paid slot subscription is required before assigning a Space."
    : activeSlots.length === 1
      ? "$12.99/month for 1 paid slot"
      : "$38.99/month for 3 paid slots";

  return (
    <div className="billing-admin-stack">
      <div className="billing-model-note">
        <strong>Paid Space slot model</strong>
        <p>
          {subscriptionLabel} is the current subscription state placeholder:
          {" "}{subscriptionPrice} for {activeSlots.length} paid Space {activeSlots.length === 1 ? "slot" : "slots"}.
          Spaces 1 gives 1 paid slot. Spaces 3 gives 3 paid slots.
        </p>
        <p>
          Each slot can be assigned to one owned Space. Paid Spaces do not count
          against Free or Plus personal Space creation caps.
        </p>
        <p>{monthlyPerPaidSpace}. This is a slot assignment model, not repeated checkout for the same subscription.</p>
      </div>
      <div className="space-overview-grid">
        <OverviewFact label="Current subscription state" value={`${subscriptionLabel} - ${subscriptionPrice}`} />
        <OverviewFact label="Available paid slots" value={String(activeSlots.length)} />
        <OverviewFact label="Assigned paid slots" value={String(assignedSlots.length)} />
        <OverviewFact label="Unassigned slots" value={String(unassignedSlots.length)} />
        <OverviewFact label="Current Space slot" value={currentSpaceSlot ? `Slot ${currentSpaceSlot.slot_index}` : "None assigned"} />
        <OverviewFact label="Renewal state placeholder" value={renewalLabel(activeSlots)} />
      </div>
      <div className={downgradePressure ? "billing-warning-note is-active" : "billing-warning-note"}>
        <strong>{downgradePressure ? "Downgrade warning" : "Downgrade warning placeholder"}</strong>
        <p>
          {downgradePressure
            ? "There are fewer active paid slots than Spaces currently marked as paid. Choose which Spaces keep a paid slot before expiry to avoid Free Limit Mode surprises."
            : "If a paid slot is removed, the Space may enter Free Limit Mode when it exceeds Free limits. Paid features continue until the end of the billing period in this placeholder model."}
        </p>
        <button className="button secondary" onClick={onChooseDowngradeSpace} type="button">
          Choose downgrade Space placeholder
        </button>
      </div>
      <div className="billing-warning-note">
        <strong>Free Limit Mode</strong>
        <p>
          Free Limit Mode gives a 14-day grace period when a downgraded paid Space
          no longer fits Free limits. Admins should reduce members, Rooms, or paid
          slot assignments before the deadline.
        </p>
      </div>
      {showDevBillingControls ? (
        <div className="dev-billing-panel" aria-label="Development-only fake billing controls">
          <div>
            <strong>Development-only fake billing controls</strong>
            <p>
              These controls call local/mock fake billing methods for staging and
              development. They are hidden in production builds.
            </p>
          </div>
          <div className="dev-billing-actions">
            <button className="button secondary" onClick={() => onFakePurchase("booply_plus_monthly")} type="button">
              Activate Plus
            </button>
            <button className="button secondary" onClick={() => onFakePurchase("booply_spaces_1_monthly")} type="button">
              Activate Spaces 1
            </button>
            <button className="button secondary" onClick={() => onFakePurchase("booply_spaces_3_monthly")} type="button">
              Activate Spaces 3
            </button>
            <button className="button secondary" onClick={onExpireFakeSubscription} type="button">
              Expire subscription
            </button>
          </div>
          <dl className="dev-billing-products">
            {billingProducts.map((product) => (
              <div key={product.product_id}>
                <dt>{product.display_name}</dt>
                <dd>{formatUsdCents(product.price_usd_cents)} / month - {product.paid_space_slots} paid {product.paid_space_slots === 1 ? "slot" : "slots"}</dd>
              </div>
            ))}
          </dl>
          <p className="form-note">
            Fake subscriptions in this session: {fakeSubscriptions.length === 0
              ? "none yet"
              : fakeSubscriptions.map((subscription) => `${formatBillingProductId(subscription.product_id)} ${subscription.status}`).join(", ")}
          </p>
        </div>
      ) : null}
      {message ? <p className="form-note">{message}</p> : null}
      <section className="paid-slot-section" aria-label="Unassigned paid slots">
        <h3>Unassigned paid slots</h3>
        <div className="paid-slot-list">
          {unassignedSlots.length === 0 ? (
            <p className="form-note">No unassigned active paid slots are available.</p>
          ) : unassignedSlots.map((slot) => {
            const canAssignToCurrentSpace = currentSpaceSlot === null;

            return (
              <article className="paid-slot-row" key={slot.paid_slot_id}>
                <div>
                  <strong>Slot {slot.slot_index}</strong>
                  <span>Available for one owned Space</span>
                </div>
                <dl>
                  <div>
                    <dt>Subscription</dt>
                    <dd>{slot.subscription_id}</dd>
                  </div>
                  <div>
                    <dt>Assigned Space</dt>
                    <dd>Unassigned</dd>
                  </div>
                  <div>
                    <dt>Renews</dt>
                    <dd>{slot.current_period_ends_at ? formatDate(slot.current_period_ends_at) : "Renewal placeholder"}</dd>
                  </div>
                </dl>
                <div className="paid-slot-actions">
                  <button className="button primary" disabled={!canAssignToCurrentSpace} onClick={() => onAssignSlot(slot.paid_slot_id)} type="button">
                    Assign slot to Space
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <section className="paid-slot-section" aria-label="Assigned paid slots">
        <h3>Assigned paid slots</h3>
        <div className="paid-slot-list">
        {assignedSlots.length === 0 ? (
          <p className="form-note">No paid slots are assigned.</p>
        ) : assignedSlots.map((slot) => {
          const assignedSpace = spaces.find((candidate) => candidate.space_id === slot.assigned_space_id);
          const assignedToCurrentSpace = slot.assigned_space_id === currentSpace.space_id;

          return (
            <article className="paid-slot-row" key={slot.paid_slot_id}>
              <div>
                <strong>Slot {slot.slot_index}</strong>
                <span>{slotStatusLabel(slot, assignedSpace)}</span>
              </div>
              <dl>
                <div>
                  <dt>Subscription</dt>
                  <dd>{slot.subscription_id}</dd>
                </div>
                <div>
                  <dt>Assigned Space</dt>
                  <dd>{assignedSpace ? assignedSpace.name : "Unassigned"}</dd>
                </div>
                <div>
                  <dt>Renews</dt>
                  <dd>{slot.current_period_ends_at ? formatDate(slot.current_period_ends_at) : "Renewal placeholder"}</dd>
                </div>
              </dl>
              <div className="paid-slot-actions">
                <button className="button danger-subtle" disabled={!assignedToCurrentSpace} onClick={() => onUnassignSlot(slot.paid_slot_id)} type="button">
                  Unassign slot
                </button>
              </div>
            </article>
          );
        })}
        </div>
      </section>
    </div>
  );
}

interface OverviewFactProps {
  readonly label: string;
  readonly value: string;
}

function OverviewFact({ label, value }: OverviewFactProps) {
  return (
    <article className="overview-fact">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

function formatUsdCents(value: number): string {
  return `$${(value / 100).toFixed(value % 100 === 0 ? 0 : 2)}`;
}

function formatBillingProductId(productId: BillingProduct["product_id"]): string {
  switch (productId) {
    case "booply_plus_monthly":
      return "Plus";
    case "booply_spaces_1_monthly":
      return "Spaces 1";
    case "booply_spaces_3_monthly":
      return "Spaces 3";
  }
}

function isDevBillingControlVisible(): boolean {
  return isWebDevelopmentControlVisible();
}

function formatVerificationState(state: MockJoinRequest["verification_state"]): string {
  switch (state) {
    case "device_seen":
      return "Device seen";
    case "not_checked":
      return "Not checked";
    case "verified_by_admin":
      return "Verified by admin";
  }
}

function formatReportReason(reason: string): string {
  return reason
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function confirmReportAction(title: string, body: string): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  return window.confirm(`${title}\n\n${body}`);
}

function memberDisplayLabel(member: MockMember): string {
  const handle = member.handle ? ` @${member.handle}` : "";

  return `${member.display_name ?? member.profile_id}${handle}`;
}

function renewalLabel(slots: readonly PaidSpaceSlot[]): string {
  const nextRenewal = slots.find((slot) => slot.current_period_ends_at)?.current_period_ends_at;

  return nextRenewal ? `Renews ${formatDate(nextRenewal)}` : "Renewal placeholder";
}

function slotStatusLabel(slot: PaidSpaceSlot, assignedSpace: MockSpace | undefined): string {
  if (!slot.active) {
    return "Inactive slot placeholder";
  }

  if (assignedSpace) {
    return `Assigned to ${assignedSpace.name}`;
  }

  return "Unassigned paid slot";
}

function spaceRetentionInitialValue(space: MockSpace): number {
  return space.retention_days;
}

function statusChipClass(status: MockMember["status"]): string {
  switch (status) {
    case "active":
      return "status-chip success";
    case "banned":
      return "status-chip warning";
    case "removed":
      return "status-chip quiet";
  }
}

function toUpdatedRoom(
  room: MockRoom,
  name: string,
  description: string,
  draft: RoomDraft,
  canUseRoleRestrictedRooms: boolean
): MockRoom {
  const roleRestricted = draft.roleRestricted && canUseRoleRestrictedRooms;

  return {
    ...room,
    allowed_roles: roleRestricted ? draft.allowedRoles : [],
    description: description.length > 0 ? description : null,
    name,
    role_restricted: roleRestricted,
    room_type: roleRestricted ? "role_based" : "standard",
    updated_at: new Date().toISOString()
  };
}

function toggleRole(currentRoles: readonly SpaceRole[], role: SpaceRole, enabled: boolean): readonly SpaceRole[] {
  if (enabled) {
    return currentRoles.includes(role) ? currentRoles : [...currentRoles, role];
  }

  const nextRoles = currentRoles.filter((currentRole) => currentRole !== role);
  return nextRoles.length > 0 ? nextRoles : ["owner"];
}

function inviteLimitCopy(planState: string, isPaidSpace: boolean): string {
  if (isPaidSpace) {
    return "Paid Spaces support multiple active invites, Guest Passes, and QR poster workflows.";
  }

  if (planState === "plus") {
    return `Plus Spaces can have up to ${PLUS_INVITE_LIMIT} active join codes or invite links. QR invite support remains available.`;
  }

  return "Free Spaces can have 1 active join code, 1 active invite link, and a QR invite.";
}

function initials(value: string): string {
  return value
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
